"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const TONES = ["Professional", "Friendly", "Bold", "Playful"] as const;

interface StepBrandKitProps {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export function StepBrandKit({ brandId, onContinue, onSkip, onBack }: StepBrandKitProps) {
  const { t } = useTranslation(["wizard", "common"]);
  const [primaryColor, setPrimaryColor] = useState("#6366f1");
  const [tone, setTone] = useState<string>("");
  const [saving, setSaving] = useState(false);

  async function handleContinue() {
    setSaving(true);
    try {
      await fetch(`/api/brands/${brandId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ primaryColor, toneOfVoice: tone }),
      });
    } finally {
      setSaving(false);
      onContinue();
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-[18px] font-bold text-foreground tracking-tight">{t("steps.brandKit.title", { ns: "wizard" })}</h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          {t("steps.brandKit.description", { ns: "wizard" })}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>{t("steps.brandKit.primaryColorLabel", { ns: "wizard" })}</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="w-10 h-10 rounded-lg border border-border cursor-pointer p-0.5"
            />
            <span className="text-[13px] font-mono text-muted-foreground">{primaryColor}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>
            {t("steps.brandKit.toneLabel", { ns: "wizard" })}{" "}
            <span className="text-muted-foreground font-normal">{t("optional", { ns: "common" })}</span>
          </Label>
          <div className="flex flex-wrap gap-2">
            {TONES.map((toneValue) => (
              <button
                key={toneValue}
                type="button"
                onClick={() => setTone(tone === toneValue ? "" : toneValue)}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors ${
                  tone === toneValue
                    ? "bg-brand-subtle border-brand text-brand-subtle-text"
                    : "bg-card border-border text-foreground hover:bg-muted"
                }`}
              >
                {t(`steps.brandKit.tones.${toneValue}`, { ns: "wizard" })}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <Button variant="secondary" onClick={onBack}>{t("nav.back", { ns: "wizard" })}</Button>
        <Button className="flex-1" onClick={handleContinue} disabled={saving}>
          {saving ? t("actions.saving", { ns: "common" }) : t("nav.continue", { ns: "wizard" })}
        </Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={onSkip}>
          {t("nav.skip", { ns: "wizard" })}
        </Button>
      </div>
    </div>
  );
}
